import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { DailyReportService } from '../../../../daily-report.service';
import { takeUntil } from 'rxjs/operators';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { CancelExternalTestRequest } from '../../../../../../../../core/projects.actions';
import { CancelTestDialogComponent } from '../cancel-test-dialog/cancel-test-dialog.component';
import { ReportService } from '../../../../../../../../../../shared/services';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'app-external-test-details',
    templateUrl: './external-test-details.component.html',
    styleUrls: ['./external-test-details.component.scss'],
    standalone: false
})
export class ExternalTestDetailsComponent implements OnInit, OnDestroy {
  @Input() data: any;
  accordionConfig = [
    {
      id: 'accordion_1',
      heading: 'Request',
      subHeading: 'sub heading',
      isActive: false,
      isDisabled: false,
    },
    {
      id: 'accordion_2',
      heading: 'Schedule',
      subHeading: 'sub heading',
      isActive: false,
      isDisabled: false,
    },
    {
      id: 'accordion_3',
      heading: 'Record',
      subHeading: 'sub heading',
      isActive: false,
      isDisabled: false,
    },
    {
      id: 'accordion_4',
      heading: 'Report',
      subHeading: 'sub heading',
      isActive: false,
      isDisabled: false,
    },
    {
      id: 'accordion_5',
      heading: 'Result',
      subHeading: 'sub heading',
      isActive: false,
      isDisabled: false,
    },
  ];
  selectedAccordionId: any;
  public downloadingReport = false;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private dailyReportService: DailyReportService,
    private cdRef: ChangeDetectorRef,
    private projectsService: ProjectsService,
    private store: Store,
    private dialog: MatDialog,
    private reportService: ReportService,
    private snackBar: MatSnackBar,
  ) {

  }

  ngOnInit() {
    this.dailyReportService.disableAddMenu.next(true);
    this.updateAccordion(this.data?.externalTestDetails);

    this.projectsService.changeView
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data?.id) {
          this.selectedAccordionId = null;
          this.updateAccordion(data);
        }
      });
  }

  expand(accordion) {
    if (this.selectedAccordionId === accordion.id) {
      this.selectedAccordionId = null;
    } else {
      this.selectedAccordionId = accordion.id;
    }
  }

  updateAccordion(data) {
    this.accordionConfig.forEach(o => {
      o.isDisabled = false;
      o.isActive = false;
    });

    const disable = (...ids) => {
      this.accordionConfig.forEach(o => {
        if (ids.includes(o.id)) {
          o.isDisabled = true;
        }
      });
    };

    const activate = (id) => {
      this.accordionConfig.forEach(o => {
        o.isActive = (o.id === id);
      });
    };

    switch (data?.status) {

      case 'sent_to_lab':
        activate('accordion_2');
        disable('accordion_3', 'accordion_4', 'accordion_5');
        break;

      case 'lab_scheduled':
        activate('accordion_3');
        disable('accordion_4', 'accordion_5');
        break;

      case 'lab_activity_completed':
        activate('accordion_4');
        disable('accordion_5');
        break;

      case 'passed':
      case 'failed':
        activate('accordion_5');
        break;

      case 'cancelled': {
        // Show completed stages based on activity log
        const log = data?.activity_log || [];
        const has = (type) => log.some(e => e.type === type);
        if (has('updated_report')) {
          // was at report stage
          disable('accordion_5');
        } else if (has('lab_activity_completed')) {
          disable('accordion_4', 'accordion_5');
        } else if (has('lab_scheduled')) {
          disable('accordion_3', 'accordion_4', 'accordion_5');
        } else if (has('sent_to_lab')) {
          disable('accordion_3', 'accordion_4', 'accordion_5');
        } else {
          disable('accordion_1', 'accordion_2', 'accordion_3', 'accordion_4', 'accordion_5');
        }
        activate('accordion_1');
        break;
      }
    }

    this.cdRef.detectChanges();
  }

  get canCancel(): boolean {
    const status = this.data?.externalTestDetails?.status;
    return ['sent_to_lab', 'lab_scheduled', 'lab_activity_completed', 'updated_report'].includes(status);
  }

  cancelTest(): void {
    const dialogRef = this.dialog.open(CancelTestDialogComponent);

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        const testId = this.data?.externalTestDetails?.id;
        this.store.dispatch(CancelExternalTestRequest({
          payload: { testId, comment: result }
        }));
      }
    });
  }

  /**
   * Download a single-test PDF for this offsite test. Resolves the offsite-test
   * report template at the project or org level, then requests the PDF filtered
   * to this test's uuid.
   */
  async downloadPdf(): Promise<void> {
    if (this.downloadingReport) {
      return;
    }
    const details = this.data?.externalTestDetails;
    const testUuid = details?.id || details?.uuid;
    const projectId = details?.project?.id || details?.project_id;

    if (!testUuid || !projectId) {
      this.snackBar.open('Missing test or project context.', '', { duration: 5000 });
      return;
    }

    this.downloadingReport = true;
    try {
      const report = await this.reportService.resolveEntityReport(projectId, 'offsite_test');
      if (!report) {
        this.snackBar.open(
          'No offsite test report template is configured. Please contact your administrator.',
          '',
          { duration: 5000 },
        );
        return;
      }
      await this.reportService.downloadReport(
        report,
        null,
        { id: projectId },
        { testIds: [testUuid], attachments: true },
        true,
        false,
      );
      this.snackBar.open('Report downloaded successfully', '', { duration: 3000 });
    } catch {
      this.snackBar.open('Failed to download report', '', { duration: 5000 });
    } finally {
      this.downloadingReport = false;
      this.cdRef.detectChanges();
    }
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

}
